import React, { useState } from "react";
import { Trophy, Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppModal, Callout, ConfirmModal, EmptyState, FormField, IconButton,
  Input, NativeSelect, Textarea,
} from "../ui";
import { ACHIEVEMENT_TYPES, fmtDate, labelFor } from "./constants";
import { achievementsApi, errorText } from "./portfolioApi";

/**
 * AchievementsSection — awards, hackathons, publications, volunteering.
 *
 * Kept separate from certifications on purpose. An achievement has no issuer to
 * verify against and no expiry date; a certification has both. Merging them
 * produced rows with half their fields blank whichever way round they were
 * filled in, which is why the two shapes are two schemas on the server too.
 */

const EMPTY = { title: "", type: "award", issuer: "", date: "", description: "", url: "" };

const toForm = (a) => ({
  title: a.title || "",
  type: a.type || "award",
  issuer: a.issuer || "",
  date: a.date ? a.date.slice(0, 10) : "",
  description: a.description || "",
  url: a.url || "",
});

const AchievementsSection = ({ achievements = [], onPortfolioChange, readOnly = false }) => {
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  // Newest first. An undated entry sorts last rather than being dropped — the
  // student may not remember the month, and that is not a reason to hide it.
  const sorted = [...achievements].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(b.date) - new Date(a.date);
  });

  const del = async () => {
    setBusy(true);
    try {
      const { portfolio } = await achievementsApi.remove(confirm._id);
      onPortfolioChange(portfolio, "Achievement removed.");
      setConfirm(null);
    } catch (err) {
      onPortfolioChange(null, errorText(err, "Failed to remove the achievement."), "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle className="[&_svg]:tw-text-primary">
            <Trophy size={18} /> Achievements
            {achievements.length > 0 && (
              <span className="tw-ml-1 tw-text-sm tw-font-normal tw-text-content-subtle">
                ({achievements.length})
              </span>
            )}
          </AppCardTitle>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
            Awards, hackathons, competitions, publications and the things you gave your time to.
          </p>
        </div>
        {!readOnly && (
          <AppButton variant="outline" size="sm" onClick={() => setModal({ mode: "add" })}>
            <Plus size={14} /> Add
          </AppButton>
        )}
      </AppCardHeader>

      <AppCardContent>
        {sorted.length === 0 ? (
          <EmptyState
            icon={Trophy}
            title="Nothing listed yet"
            description={readOnly
              ? "This student has not added any achievements."
              : "Hackathon placings, published work, a scholarship, a society you ran — all of it counts."}
            action={!readOnly && (
              <AppButton size="sm" onClick={() => setModal({ mode: "add" })}>
                <Plus size={14} /> Add an achievement
              </AppButton>
            )}
          />
        ) : (
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {sorted.map((a) => (
              <li
                key={a._id}
                className="tw-flex tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3.5"
              >
                <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-sm tw-bg-warning/10 tw-text-warning">
                  <Trophy size={17} />
                </span>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <h4 className="tw-m-0 tw-text-[0.9rem] tw-font-semibold tw-text-content">{a.title}</h4>
                    <AppBadge variant="secondary" size="sm">{labelFor(ACHIEVEMENT_TYPES, a.type)}</AppBadge>
                  </div>
                  <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-subtle">
                    {[a.issuer, a.date && fmtDate(a.date)].filter(Boolean).join(" · ") || "—"}
                  </p>
                  {a.description && (
                    <p className="tw-m-0 tw-mt-1.5 tw-text-[0.82rem] tw-leading-relaxed tw-text-content-muted">
                      {a.description}
                    </p>
                  )}
                  {a.url && (
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary tw-no-underline hover:tw-underline"
                    >
                      <ExternalLink size={12} /> View
                    </a>
                  )}
                </div>
                {!readOnly && (
                  <div className="tw-flex tw-shrink-0 tw-gap-0.5">
                    <IconButton label="Edit achievement" icon={Pencil} size="sm"
                      onClick={() => setModal({ mode: "edit", item: a })} />
                    <IconButton label="Remove achievement" icon={Trash2} size="sm"
                      variant="ghost-destructive" onClick={() => setConfirm(a)} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </AppCardContent>

      {modal && (
        <AchievementModal
          mode={modal.mode}
          item={modal.item}
          onClose={() => setModal(null)}
          onSaved={(portfolio, message) => { onPortfolioChange(portfolio, message); setModal(null); }}
        />
      )}

      <ConfirmModal
        show={!!confirm}
        onHide={() => !busy && setConfirm(null)}
        onConfirm={del}
        loading={busy}
        variant="danger"
        title="Remove this achievement?"
        message={confirm ? `"${confirm.title}" will be removed from your portfolio.` : ""}
        confirmText="Remove"
      />
    </AppCard>
  );
};

const AchievementModal = ({ mode, item, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (item ? toForm(item) : EMPTY));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("A title is required."); return; }
    setSaving(true);
    setError("");
    try {
      const { portfolio, message } = mode === "add"
        ? await achievementsApi.add(form)
        : await achievementsApi.update(item._id, form);
      onSaved(portfolio, message);
    } catch (err) {
      setError(errorText(err, "Failed to save the achievement."));
      setSaving(false);
    }
  };

  return (
    <AppModal
      show
      onHide={onClose}
      title={mode === "add" ? "Add an achievement" : "Edit achievement"}
      icon={<Trophy size={18} />}
      footer={
        <div className="tw-flex tw-justify-end tw-gap-2">
          <AppButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</AppButton>
        </div>
      }
    >
      <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={submit}>
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Title" required>
          <Input value={form.title} onChange={(e) => set({ title: e.target.value })}
            placeholder="e.g. Runner-up, Smart India Hackathon 2026" maxLength={200} autoFocus />
        </FormField>

        <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
          <FormField label="Type">
            <NativeSelect value={form.type} onChange={(e) => set({ type: e.target.value })}
              options={ACHIEVEMENT_TYPES} />
          </FormField>
          <FormField label="Awarded by">
            <Input value={form.issuer} onChange={(e) => set({ issuer: e.target.value })}
              placeholder="Organisation or event" maxLength={160} />
          </FormField>
        </div>

        <FormField label="Date">
          <Input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
        </FormField>

        <FormField label="Description">
          <Textarea rows={3} value={form.description}
            onChange={(e) => set({ description: e.target.value })} maxLength={2000} />
        </FormField>

        <FormField label="Link" hint="A certificate, an announcement, a published article">
          <Input type="url" value={form.url} onChange={(e) => set({ url: e.target.value })}
            placeholder="https://…" />
        </FormField>
      </form>
    </AppModal>
  );
};

export default AchievementsSection;
